import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useRole } from "@/contexts/RoleContext";
import { getTeacherFmPanel, getTeacherFmTimeStats, teacherFmUnlock } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  ArrowLeft, Layers, Lock, CheckCircle2, Loader2, Minus, Unlock, TrendingUp, Users, Clock, ChevronDown, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import FMTestGeneratorModal from "@/components/FMTestGeneratorModal";

const SKILLS = ["Addition", "Subtraction", "Multiplication", "Division"];
const SKILL_SYMBOL = { Addition: "+", Subtraction: "−", Multiplication: "×", Division: "÷" };

// ── State badge config ─────────────────────────────────────────────────────────
const STATE_BADGE = {
  not_started: { label: "Not Started", cls: "bg-slate-100 text-slate-500", icon: <Minus className="w-3 h-3" /> },
  locked:      { label: "Locked",      cls: "bg-red-100 text-red-600",     icon: <Lock className="w-3 h-3" /> },
  in_progress: { label: "In Progress", cls: "bg-fuchsia-100 text-fuchsia-700", icon: <TrendingUp className="w-3 h-3" /> },
  mastered:    { label: "Mastered",    cls: "bg-amber-100 text-amber-700", icon: <CheckCircle2 className="w-3 h-3" /> },
};

function SkillCell({ skill, data, learnerId, teacherId, onUnlocked }) {
  const [unlocking, setUnlocking] = useState(false);
  const badge = STATE_BADGE[data.state] || STATE_BADGE.not_started;
  const isLocked = data.state === "locked";
  // Show unlock for locked cells OR not_started cells where teacher hasn't explicitly unlocked
  const canUnlock = isLocked || (data.state === "not_started" && !data.teacher_unlocked);

  const handleUnlock = async () => {
    setUnlocking(true);
    try {
      await teacherFmUnlock(teacherId, learnerId, skill);
      toast.success(`${skill} unlocked`);
      onUnlocked(learnerId, skill);
    } catch {
      toast.error("Unlock failed");
    } finally {
      setUnlocking(false);
    }
  };

  return (
    <td className="px-4 py-3 align-top" data-testid={`fm-cell-${learnerId}-${skill.toLowerCase()}`}>
      <div className="flex flex-col gap-1">
        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full w-fit ${badge.cls}`}>
          {badge.icon} {badge.label}
        </span>

        {data.state === "in_progress" && data.current_level && (
          <span className="text-xs text-fuchsia-600 font-bold pl-1">
            {data.current_level}
          </span>
        )}

        {isLocked && data.lock_reason_label && (
          <span className="text-xs text-red-400 pl-1">{data.lock_reason_label}</span>
        )}

        {canUnlock && (
          <Button
            size="sm"
            variant="outline"
            className="mt-1 h-6 text-xs px-2 rounded-full border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50 w-fit"
            onClick={handleUnlock}
            disabled={unlocking}
            data-testid={`fm-unlock-btn-${learnerId}-${skill.toLowerCase()}`}
          >
            {unlocking ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Unlock className="w-3 h-3 mr-1" />Unlock</>}
          </Button>
        )}
      </div>
    </td>
  );
}

export default function TeacherFMPanel() {
  const { grade } = useParams();
  const { teacherId } = useRole();
  const gradeNum = parseInt(grade, 10);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeStats, setTimeStats] = useState(null);
  const [timingOpen, setTimingOpen] = useState(false);
  const [showFMModal, setShowFMModal] = useState(false);

  // Listen for "Generate FM Test" button from AppHeader
  useEffect(() => {
    const handleOpen = () => setShowFMModal(true);
    window.addEventListener("openFMTestGenerator", handleOpen);
    return () => window.removeEventListener("openFMTestGenerator", handleOpen);
  }, []);

  const load = useCallback(() => {
    if (!teacherId) return;
    setLoading(true);
    Promise.all([
      getTeacherFmPanel(teacherId, gradeNum),
      getTeacherFmTimeStats(teacherId, gradeNum),
    ])
      .then(([panel, stats]) => { setData(panel); setTimeStats(stats); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [teacherId, gradeNum]);

  useEffect(() => { load(); }, [load]);

  // Optimistic state update after unlock
  const handleUnlocked = (learnerId, skill) => {
    setData(prev => {
      if (!prev) return prev;
      const updatedLearners = prev.learners.map(ln => {
        if (ln.learner_id !== learnerId) return ln;
        return {
          ...ln,
          skills: {
            ...ln.skills,
            [skill]: {
              ...ln.skills[skill],
              state: "not_started",
              teacher_unlocked: true,
              lock_reason: null,
              lock_reason_label: null,
            },
          },
        };
      });
      const locked = updatedLearners.reduce(
        (acc, ln) => acc + Object.values(ln.skills).filter(s => s.state === "locked").length, 0
      );
      return { ...prev, learners: updatedLearners, summary: { ...prev.summary, locked } };
    });
  };

  return (
    <>
    <div className="teacher-scope min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="teacher-fm-panel">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link
              to="/teacher"
              className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-2 transition-colors"
              data-testid="fm-panel-back"
            >
              <ArrowLeft className="w-4 h-4" /> Back to cockpit
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-fuchsia-100 text-fuchsia-700 flex items-center justify-center">
                <Layers className="w-5 h-5" />
              </div>
              <div>
                <h1 className="font-teacher text-2xl md:text-3xl font-bold text-slate-900" data-testid="fm-panel-title">
                  Math Foundations
                </h1>
                <p className="text-slate-500 text-sm">Grade {gradeNum} · Number skills overview</p>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-fuchsia-400 animate-spin" />
          </div>
        ) : !data || data.learners.length === 0 ? (
          <Card className="rounded-2xl p-10 text-center border border-slate-200 bg-white text-slate-500">
            No learners found for Grade {gradeNum}.
          </Card>
        ) : (
          <>
            {/* Summary bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="fm-panel-summary">
              {[
                { label: "Learners", value: data.summary.total, icon: <Users className="w-4 h-4" />, cls: "text-slate-700" },
                { label: "Locked",   value: data.summary.locked,      icon: <Lock className="w-4 h-4" />,  cls: "text-red-600" },
                { label: "In Progress", value: data.summary.in_progress, icon: <TrendingUp className="w-4 h-4" />, cls: "text-fuchsia-700" },
                { label: "Mastered", value: data.summary.mastered,    icon: <CheckCircle2 className="w-4 h-4" />, cls: "text-amber-600" },
              ].map(s => (
                <Card key={s.label} className="rounded-2xl p-4 border border-slate-100 bg-white flex items-center gap-3">
                  <span className={s.cls}>{s.icon}</span>
                  <div>
                    <div className={`text-xl font-bold ${s.cls}`}>{s.value}</div>
                    <div className="text-xs text-slate-500">{s.label}</div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Skills header legend */}
            <div className="flex gap-2 mb-4 flex-wrap">
              {SKILLS.map(skill => (
                <span key={skill} className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-fuchsia-50 border border-fuchsia-200 text-fuchsia-700">
                  <span className="font-black">{SKILL_SYMBOL[skill]}</span> {skill}
                </span>
              ))}
            </div>

            {/* Table */}
            <div className="rounded-2xl border border-slate-200 overflow-hidden bg-white">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="fm-learner-table">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wide">
                        Learner
                      </th>
                      {SKILLS.map(skill => (
                        <th key={skill} className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wide">
                          <span className="font-black text-fuchsia-500 mr-1">{SKILL_SYMBOL[skill]}</span>
                          {skill}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.learners.map((ln, idx) => (
                      <tr
                        key={ln.learner_id}
                        className={`border-b border-slate-100 ${idx % 2 === 0 ? "bg-white" : "bg-slate-50/30"} hover:bg-fuchsia-50/20 transition-colors`}
                        data-testid={`fm-learner-row-${ln.learner_id}`}
                      >
                        <td className="px-4 py-3 font-semibold text-slate-800">
                          <div>{ln.name}</div>
                          <div className="text-xs text-slate-400 font-normal">{ln.learner_id}</div>
                        </td>
                        {SKILLS.map(skill => (
                          <SkillCell
                            key={skill}
                            skill={skill}
                            data={ln.skills[skill] || { state: "not_started" }}
                            learnerId={ln.learner_id}
                            teacherId={teacherId}
                            onUnlocked={handleUnlocked}
                          />
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* ── Timing Analysis ────────────────────────────────────────────────── */}
        {!loading && timeStats && (
          <div className="mt-6" data-testid="fm-timing-section">
            <button
              className="w-full flex items-center justify-between px-5 py-3.5 rounded-2xl bg-white border border-slate-200 hover:border-fuchsia-300 transition-colors group"
              onClick={() => setTimingOpen(o => !o)}
              data-testid="fm-timing-toggle"
            >
              <div className="flex items-center gap-2.5">
                <Clock className="w-4 h-4 text-fuchsia-500" />
                <span className="font-semibold text-slate-700 text-sm">Average Solving Time — per Skill × Level</span>
                <span className="text-xs text-slate-400">(class average across all learners)</span>
              </div>
              {timingOpen
                ? <ChevronUp className="w-4 h-4 text-slate-400" />
                : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>

            {timingOpen && (
              <div className="mt-3 rounded-2xl border border-slate-200 overflow-hidden bg-white" data-testid="fm-timing-grid">
                {SKILLS.map(skill => {
                  const levels = timeStats.skills?.[skill] || {};
                  const levelNums = Object.keys(levels).map(Number).sort((a, b) => a - b);
                  const hasData = levelNums.length > 0;

                  return (
                    <div key={skill} className="border-b border-slate-100 last:border-0">
                      {/* Skill row header */}
                      <div className="flex items-center gap-2 px-5 py-2.5 bg-slate-50 border-b border-slate-100">
                        <span className="font-black text-fuchsia-500 text-base w-5 text-center">{SKILL_SYMBOL[skill]}</span>
                        <span className="font-semibold text-slate-700 text-sm">{skill}</span>
                        {!hasData && (
                          <span className="text-xs text-slate-400 ml-2">No timing data yet</span>
                        )}
                      </div>

                      {hasData && (
                        <div className="flex gap-3 px-5 py-3 overflow-x-auto">
                          {levelNums.map(lnum => {
                            const cell = levels[lnum];
                            // Colour-code: ≤20s green, 21-45s amber, >45s red
                            const sec = cell.avg_sec;
                            const color = sec <= 20
                              ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                              : sec <= 45
                                ? "bg-amber-50 border-amber-200 text-amber-700"
                                : "bg-red-50 border-red-200 text-red-700";

                            return (
                              <div
                                key={lnum}
                                className={`flex-shrink-0 rounded-xl border px-3 py-2.5 text-center min-w-[72px] ${color}`}
                                title={`${cell.level_code} · ${cell.attempts} attempts · ${cell.learner_count} learner${cell.learner_count !== 1 ? "s" : ""} · min ${cell.min_sec}s / max ${cell.max_sec}s`}
                                data-testid={`fm-timing-cell-${skill.toLowerCase()}-${lnum}`}
                              >
                                <div className="text-[10px] font-bold uppercase tracking-wide opacity-70 mb-0.5">
                                  {cell.level_code}
                                </div>
                                <div className="text-lg font-black leading-none">
                                  {sec < 60 ? `${sec}s` : `${Math.floor(sec / 60)}m${Math.round(sec % 60)}s`}
                                </div>
                                <div className="text-[10px] opacity-60 mt-0.5">
                                  avg · {cell.attempts} ans
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Legend */}
                <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex items-center gap-4 text-xs text-slate-500">
                  <Clock className="w-3.5 h-3.5 text-slate-400" />
                  <span className="font-semibold">Time thresholds:</span>
                  <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-200 inline-block"></span> ≤20s fast</span>
                  <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-200 inline-block"></span> 21–45s moderate</span>
                  <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-red-200 inline-block"></span> &gt;45s slow</span>
                  <span className="ml-auto">Hover a cell for min/max details</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>

    {showFMModal && (
      <FMTestGeneratorModal
        gradeNum={gradeNum}
        onClose={() => setShowFMModal(false)}
      />
    )}
    </>
  );
}
